import {useInfiniteQuery} from "@tanstack/react-query";

import {
  DIRECT_MESSAGE_THREADS_PAGE_SIZE,
  directMessagesService,
} from "../services";

import {
  DIRECT_MESSAGE_THREADS_QUERY_KEY,
  type DirectMessageListOptions,
} from "./use-direct-message-threads";

const INFINITE_THREADS_QUERY_KEY = "infinite-directmessage-threads";

type UseInfiniteDirectMessageThreadsParams = {
  mergeSent?: boolean;
  unread?: boolean;
  enabled?: boolean;
};

export function useInfiniteDirectMessageThreads({
  mergeSent = false,
  unread = false,
  enabled = true,
}: UseInfiniteDirectMessageThreadsParams = {}) {
  const listOptions: DirectMessageListOptions = {mergeSent, unread};

  const {data, ...query} = useInfiniteQuery({
    queryKey: [INFINITE_THREADS_QUERY_KEY, listOptions],
    enabled,
    queryFn: ({pageParam = 1}) =>
      directMessagesService.getConversationThreads({
        mergeSent,
        unread,
        page: pageParam,
        limit: DIRECT_MESSAGE_THREADS_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, pages) => {
      const loadedItems = pages.reduce((acc, p) => acc + (p.data?.length ?? 0), 0);
      return loadedItems < (lastPage.meta?.total ?? 0) ? pages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });

  const threads = data?.pages.flatMap((p) => p.data ?? []) ?? [];
  const totalRecords = data?.pages[0]?.meta?.total ?? 0;

  return {
    threads,
    totalRecords,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isPending: query.isPending,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
  };
}

export {INFINITE_THREADS_QUERY_KEY};
